import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { evaluationQueryService } from "../services/evaluationQueryService";
import { evaluationQueryDisplayId } from "./EvaluationQueryCommonStore";
import { EvaluationQuery } from "../../../api/graphql";
import { EvaluationPathType } from "../../../api/graphql";
import { EvaluationDifficulty } from "../../../api/graphql";
import { EvaluationAmbiguityType } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class EvaluationQueryViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = evaluationQueryService;
  evaluationQueryInstance: EvaluationQuery | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  queryValue: string = "";
  pathTypeValue: EvaluationPathType = "" as EvaluationPathType;
  difficultyValue: EvaluationDifficulty = "" as EvaluationDifficulty;
  expectedToolValue: string = "";
  expectedArgsValue: string = "{}";
  acceptableToolsValue: string = "{}";
  expectedResponsePatternValue: string = "";
  generatedFromValue: string = "{}";
  isAmbiguousValue: boolean = false;
  ambiguityTypeValue: EvaluationAmbiguityType | "" = "" as
    | EvaluationAmbiguityType
    | "";
  disambiguationResponseValue: string = "";
  notesValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEvaluationQueryById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.evaluationQueryOne({ id });
    this.evaluationQueryInstance = result.evaluationQueryOne;
    if (this.evaluationQueryInstance) {
      this.loadValues(this.evaluationQueryInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.evaluationQueryInstance) {
      return evaluationQueryDisplayId(
        this.evaluationQueryInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EvaluationQuerySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getPathTypeOptions() {
    return this.langStore.EvaluationPathTypeOptions;
  }

  getDifficultyOptions() {
    return this.langStore.EvaluationDifficultyOptions;
  }

  getAmbiguityTypeOptions() {
    return this.langStore.EvaluationAmbiguityTypeOptions;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.evaluationQueryInstance = null;
    this.queryValue = "";
    this.pathTypeValue = "" as EvaluationPathType;
    this.difficultyValue = "" as EvaluationDifficulty;
    this.expectedToolValue = "";
    this.expectedArgsValue = "{}";
    this.acceptableToolsValue = "{}";
    this.expectedResponsePatternValue = "";
    this.generatedFromValue = "{}";
    this.isAmbiguousValue = false;
    this.ambiguityTypeValue = "" as EvaluationAmbiguityType | "";
    this.disambiguationResponseValue = "";
    this.notesValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEvaluationQuery() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("evaluationQuery_update");
  }

  editEvaluationQuery() {
    const id = this.evaluationQueryInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/evaluationQuery/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEvaluationQuery(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEvaluationQuery(): any {
    yield this.close();
  }

  loadValues(instance: EvaluationQuery) {
    this.idValue = instance.id;
    this.queryValue = instance.query;
    this.pathTypeValue = instance.pathType;
    this.difficultyValue = instance.difficulty;
    this.expectedToolValue = instance.expectedTool;
    this.expectedArgsValue = instance.expectedArgs;
    this.acceptableToolsValue = instance.acceptableTools;
    this.expectedResponsePatternValue = instance.expectedResponsePattern || "";
    this.generatedFromValue = instance.generatedFrom;
    this.isAmbiguousValue = instance.isAmbiguous;
    this.ambiguityTypeValue =
      instance.ambiguityType || ("" as EvaluationAmbiguityType | "");
    this.disambiguationResponseValue = instance.disambiguationResponse || "";
    this.notesValue = instance.notes || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEvaluationQueryById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
